import { Dispatch, SetStateAction, useCallback } from 'react';

import { useSyncUtils } from '../../../../hooks/useSyncUtils';
import { showFlag } from '../../../../services/jira';
import { ImportMapping, ImportMappings } from '../../../../types';
import { TogglTimeEntry } from '../../../../types/toggl-api';
import { CreatedWorklog } from '../../../../utils/jiraWorklogs';

export function useSelectedTimeEntries(
  selected: number[],
  setSelected: Dispatch<SetStateAction<number[]>>,
  timeEntries: TogglTimeEntry[],
  mappings: ImportMappings,
  issueKeys: { [timeEntryId: number]: string }
) {
  const { isLoading, syncEntries, progress } = useSyncUtils();
  const allSelected =
    selected.length > 0 &&
    timeEntries
      .map((timeEntry) => timeEntry.id)
      .filter((id) => !mappings[id])
      .every((id) => selected.includes(id));

  const someSelected = selected.length > 0;

  const isSelected = useCallback(
    (id: number) => {
      return Boolean(selected.find((s) => s === id));
    },
    [selected]
  );

  function isChecked(
    timeEntryId: number,
    timeEntryMapping: ImportMapping | null | undefined
  ) {
    return isSelected(timeEntryId) || Boolean(timeEntryMapping);
  }

  function isRowSelectionDisabled(
    timeEntry: TogglTimeEntry,
    timeEntryMapping?: ImportMapping | null
  ) {
    return (
      Boolean(timeEntryMapping) ||
      !issueKeys[timeEntry.id] ||
      timeEntry.duration < 60
    );
  }

  const togglOneSelected =
    (timeEntry: TogglTimeEntry, timeEntryMapping?: ImportMapping | null) =>
    () => {
      if (!isRowSelectionDisabled(timeEntry, timeEntryMapping)) {
        setSelected((prev) => {
          if (!prev.find((id) => id === timeEntry.id)) {
            return [...prev, timeEntry.id];
          } else {
            return prev.filter((id) => id !== timeEntry.id);
          }
        });
      }
    };

  const togglAllSelected = (isChecked: boolean) => {
    if (isChecked) {
      setSelected(
        timeEntries
          .filter(
            (timeEntry) =>
              !isRowSelectionDisabled(timeEntry, mappings[timeEntry.id])
          )
          .map((timeEntry) => timeEntry.id)
      );
    } else {
      setSelected([]);
    }
  };

  const syncSelected = () => {
    const toSync = selected
      .map((id) => {
        const timeEntry = timeEntries.find((timeEntry) => timeEntry.id === id);
        if (timeEntry && issueKeys[timeEntry.id]) {
          return { timeEntry, issueKey: issueKeys[timeEntry.id] };
        }
      })
      .filter(Boolean) as { timeEntry: TogglTimeEntry; issueKey: string }[];
    syncEntries(toSync)
      .then(({ unexpectedErrorsCount, wrongIssueKeysCount }) => {
        if (unexpectedErrorsCount) {
          showFlag(
            `Failed to sync ${unexpectedErrorsCount} entries due to unexpected errors.`,
            '',
            'error',
            'manual'
          );
        }
        if (wrongIssueKeysCount) {
          showFlag(
            `Failed to synchronize ${wrongIssueKeysCount} time entries as the associated issues were not found in Jira.`,
            '',
            'warning',
            'manual'
          );
        }
        const successfullySyncedCount =
          toSync.length - wrongIssueKeysCount - unexpectedErrorsCount;
        if (successfullySyncedCount === toSync.length) {
          showFlag('All selected entries synced', '', 'success');
        } else if (successfullySyncedCount) {
          showFlag(
            `Successfully synced ${successfullySyncedCount} time entries`,
            '',
            'success'
          );
        }
        setSelected([]);
      })
      .catch(console.error);
  };

  return {
    allSelected,
    someSelected,
    isSelected,
    isChecked,
    isRowSelectionDisabled,
    togglOneSelected,
    togglAllSelected,
    syncSelected,
    isSyncLoading: isLoading,
    syncProgress: progress,
  };
}

export function createNewImportMapping(worklog: CreatedWorklog) {
  return {
    togglTimeEntryId: worklog.timeEntry.id,
    jiraIssueKey: worklog.issueKey,
    jiraWorklogId: worklog.jiraWorklogId,
    timeSpentInSeconds: worklog.timeEntry.duration,
  };
}
